import React from "react";
import NoAvailablePaymentMethod from "../../../../Assets/CustomerDetailPage/NoAvailablePaymentMethod.svg";
import { useCustomerStore } from "../../../../Store/Customer";
import { ReactComponent as PlusIcon } from "../../../../Assets/ActionIcons/PlusIcon.svg";
import { ReactComponent as MinusIcon } from "../../../../Assets/ActionIcons/MinusIcon.svg";
import { ReactComponent as KebabMenuIcon } from "../../../../Assets/ActionIcons/KebabMenuIcon.svg";
import { ReactComponent as DownloadIcon } from "../../../../Assets/ActionIcons/DownloadIcon.svg";
import { ReactComponent as InfoIcon } from "../../../../Assets/ActionIcons/InfoIcon.svg";
import { ReactComponent as AsPrimaryIcon } from "../../../../Assets/CustomerDetailPage/AsPrimaryIcon.svg";
import { PaymentRoutes } from "../../../../Contracts/Routes";
import { PaymentMethodDisplayAttributes } from "../../../../Contracts/Finance";
import { useAuth0 } from "@auth0/auth0-react";
import Dropdown from "../../../../Components/Atoms/Dropdown";
import { AdyenMandatedBy } from "../../../../Contracts/AdyenSession";
import Popover from "../../../../Components/Atoms/Popover";
import { useBannerStore } from "../../../../Store/Banner";
import { BannerType } from "../../../../Contracts/Banner";
import { AdyenStoredPaymentMethod } from "../../../../Contracts/Transaction";

const CustomerAvailablePaymentMethodsCard: React.FC = () => {
  const { isLoading, customerDetailEntity, removePaymentMethod, setPaymentMethodAsPrimary } =
    useCustomerStore();

  const { getAccessTokenSilently } = useAuth0();
  const { addBanner } = useBannerStore();

  const removeMethod = async (paymentMethodId: string) => {
    if (!customerDetailEntity?.id) {
      console.error("Customer ID not found");
      return;
    }
    if (window.confirm("Are you sure you want to remove this payment method?")) {
      await removePaymentMethod(
        getAccessTokenSilently,
        parseInt(paymentMethodId),
        customerDetailEntity.id.toString()
      );
    }
  };

  const downloadPdf = async (paymentMethodSelected: AdyenStoredPaymentMethod) => {
    if (paymentMethodSelected.mandate_pdf) {
      window.open(paymentMethodSelected.mandate_pdf, "_blank");
    } else {
      console.error("Mandate PDF not found");
      addBanner({
        type: BannerType.Error,
        message: "Mandate PDF not found",
        closeable: true
      });
    }
  };

  const setAsPrimary = async (paymentMethodId: string) => {
    if (!customerDetailEntity?.id) {
      console.error("Customer ID not found");
      return;
    }
    if (window.confirm("Are you sure you want to set this payment method as primary?")) {
      await setPaymentMethodAsPrimary(
        getAccessTokenSilently,
        parseInt(paymentMethodId),
        customerDetailEntity.id.toString()
      );
    }
  };

  const availablePaymentMethods =
    customerDetailEntity?.available_payment_methods?.data
      .sort((a, _) => (a.primary ? -1 : 1))
      .map(m => {
        return { ...new PaymentMethodDisplayAttributes(m), ...m };
      }) || [];

  return (
    <div className="rounded p-3 border border-gray-300 my-2">
      <div className="flex items-center justify-between mb-4">
        <span className="font-semibold">Available payment methods</span>
        {customerDetailEntity?.id && (
          <a
            className="flex items-center space-x-2 text-blue-600 hover:scale-105"
            href={PaymentRoutes.ADD_PAYMENT_METHOD_MANDATE.replace(
              ":id",
              String(customerDetailEntity.id)
            )}
            target="_blank"
            rel="noreferrer"
          >
            <PlusIcon className="h-4 w-4" />
            <div>New method</div>
          </a>
        )}
      </div>
      {availablePaymentMethods.map(method => (
        <div
          className="relative flex items-center justify-between border-t border-gray-300"
          key={method.key}
        >
          <div
            className={`${isLoading ? "hidden" : "visible"} flex items-start p-2 space-x-4`}
            key={method.key}
          >
            <img src={method.logoSrc} alt={method.displayName} />
            <div className="flex flex-col self-center">
              <span>{method.detailsDisplayLine1}</span>
              <span>{method.detailsDisplayLine2}</span>
            </div>
            {method.mandated_by === AdyenMandatedBy.Self && (
              <Popover message="Manually added on behalf of client" position="left">
                <InfoIcon className="w-4 h-4 text-gray-500 mt-1" />
              </Popover>
            )}
            {method.primary && (
              <div className="py-1 px-2 bg-blue-100 text-blue-600 border border-blue-400 text-xs rounded">
                Primary
              </div>
            )}
          </div>
          <Dropdown
            position="bottom-right"
            options={[
              ...(!method.primary
                ? [
                    {
                      label: (
                        <div className="flex items-center space-x-2 px-4 py-2 hover:bg-gray-200">
                          <AsPrimaryIcon />
                          <div>Set as Primary</div>
                        </div>
                      ),
                      value: "set_as_primary"
                    }
                  ]
                : []),
              ...(method.mandate_pdf
                ? [
                    {
                      label: (
                        <div className="flex items-center space-x-2 px-4 py-2 hover:bg-gray-200">
                          <DownloadIcon />
                          <div>Download mandate</div>
                        </div>
                      ),
                      value: "download_mandate"
                    }
                  ]
                : []),
              {
                label: (
                  <div className="flex items-center space-x-2 px-4 py-2 hover:bg-gray-200">
                    <MinusIcon />
                    <div>Remove method</div>
                  </div>
                ),
                value: "remove_method"
              }
            ]}
            anchor={
              <div className="cursor-pointer flex flex-col items-center">
                <KebabMenuIcon className="text-blue-600" />
              </div>
            }
            onChange={value => {
              if (value === "set_as_primary") {
                setAsPrimary(method.key);
              } else if (value === "remove_method") {
                removeMethod(method.key);
              } else if (value === "download_mandate") {
                downloadPdf(method);
              }
            }}
          />
        </div>
      ))}
      {isLoading &&
        [1, 2, 3].map(index => (
          <div key={index} className="p-2 border-t border-gray-300 flex items-center animate-pulse">
            <div className="bg-slate-200 h-6 w-6 rounded-full mr-4"></div>
            <div className="bg-slate-200 h-6 w-20 rounded-lg"></div>
          </div>
        ))}
      {!isLoading && availablePaymentMethods.length === 0 && (
        <div className="flex flex-col items-center justify-center border-t border-gray-300 p-4">
          <img src={NoAvailablePaymentMethod} alt="no available payment methods" />
          <span>Client has not added any payment method</span>
        </div>
      )}
    </div>
  );
};

export default CustomerAvailablePaymentMethodsCard;
